/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, signal} from '@angular/core';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import JSZip from 'jszip';
import {EsciComponent} from '../esci.component';
import {MenuItem} from 'primeng/api';
import {BuildToolHarness} from '../../model/buildToolHarness.model';
import {VehicleBuildOptions} from '../../model/vehicleBuildOptions.model';
import {ModelYear} from '../../model/modelYear.model';
import {Program} from '../../model/program';
import {CommonHelperService} from '../../service/common-helper.service';

@Component({
    selector: 'app-build-check-tool',
    templateUrl: './build-check-tool.component.html',
    styleUrls: ['./build-check-tool.component.scss'],
    standalone: false
})
export class BuildCheckToolComponent extends EsciComponent implements OnInit {
  harnessMatrixFiles = new Array<File>;
  vehicleBuildFile: File;
  kitMatrixFile: File;
  showFileInput = true;
  harnessMatrixFileBlob: Blob | null;
  progress = signal(0);
  harnesses = new Array<BuildToolHarness>;
  selectedHarness?: BuildToolHarness;
  selectedVehicleBuildOptions = new Array<VehicleBuildOptions>;
  permissionUrl = '/esciUI/buildCheckToolPermission';
  adminPermissionUrl = '/esciUI/buildCheckToolAdminPermission';
  // This allows admins to bypass validation, it is a parameter we pass to the GMUtilLib method
  bypassValidation: any;

  // Harness Organization
  vaaKits = new Array<BuildToolHarness>;
  harnessKits = new Array<BuildToolHarness>;
  poaKits = new Array<BuildToolHarness>;
  showOptionsComparison: boolean;
  showHarnessBuckets: boolean;
  programs = new Array<Program>;
  selectedProgram: Program;
  modelYears = new Array<ModelYear>;
  selectedModelYear: ModelYear;
  downloadButtonOptions: MenuItem[];
  private maxFileSizeMessage = ' Maximum file size is ' + Math.round(this.MAX_FILE_SIZE / 1000) + 'kb.';

  private setProgress(value: number): void {
    this.progress.set(value);
  }

  runReport(): void {
    if (this.harnessMatrixFiles?.length && this.vehicleBuildFile && this.kitMatrixFile
      && this.permission() && this.areFileSizesValid()) {
      this.showOptionsComparison = false;
      this.setProgress(10);

      this.harnesses = new Array<BuildToolHarness>;
      this.selectedHarness = new BuildToolHarness('');
      this.selectedVehicleBuildOptions = new Array<VehicleBuildOptions>;

      // Zip up the harness matrix files and then call the upload function
      const zip = new JSZip();
      for (const file of this.harnessMatrixFiles) {
        zip.file(file.name, file);
      }
      zip.generateAsync({type: 'blob'}).then((content) => {
        this.harnessMatrixFileBlob = content;
        this.uploadService.uploadFilesForReport(this.harnessMatrixFileBlob, this.vehicleBuildFile,
            this.kitMatrixFile, this.bypassValidation).subscribe({
            next: (event: any) => {
                if (event.type === HttpEventType.UploadProgress) {
                    this.setProgress(Math.max(10, Math.round(100 * event.loaded / event.total)));
                } else if (event instanceof HttpResponse) {
                    this.setProgress(0);
                    this.harnesses = event.body;
                    if (this.harnesses && this.harnesses.length > 0) {
                        BuildToolHarness.sortData(this.harnesses);
                        this.selectedHarness = this.harnesses[0];
                        this.showFileInput = false;
                        this.showOptionsComparison = true;
                        this.collectErrorMessages(this.harnesses);
                    } else {
                        this.messageService.processError('Either no vehicle builds found or files were invalid. Please try again.');
                    }
                }
            },
            error: (err: any) => {
              this.setProgress(0);
              console.log(err);
              this.messageService.processError('Could not run the build check tool. Please check the files provided and try again later.');
            }
          });
      });
    }
  }

    private collectErrorMessages(harnesses: BuildToolHarness[]) {
        if (this.adminPermission()) {
            // Group harnesses by error type and display to admins
            const errorTypeMap = new Map<string, string[]>();
            harnesses.forEach(harness => {
                if (harness.errors && harness.errors.length > 0) {
                    harness.errors.forEach(error => {
                        if (!errorTypeMap.has(error)) {
                            errorTypeMap.set(error, []);
                        }
                        errorTypeMap.get(error)?.push(harness.harnessName);
                    });
                }
            });
            if (errorTypeMap.size > 0) {
                errorTypeMap.forEach((harnessNames: string[], errorType: string) => {
                    const errorMessage = `${errorType} on Harness(es): ` + harnessNames.join(', ');
                    this.messageService.processError(errorMessage);
                });
            }
        }
    }

    processHarnesses(): void {
    if (this.harnessMatrixFiles?.length && this.vehicleBuildFile && this.permission() && this.areFileSizesValid()) {
      this.showHarnessBuckets = false;
      this.setProgress(10);

      // Zip up the harness matrix files and then call the upload function
      const zip = new JSZip();
      for (const file of this.harnessMatrixFiles) {
        zip.file(file.name, file);
      }
      zip.generateAsync({type: 'blob'}).then((content) => {
        this.harnessMatrixFileBlob = content;
        this.uploadService.uploadFilesForHarnesses(this.harnessMatrixFileBlob, this.bypassValidation).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.setProgress(Math.max(10, Math.round(100 * event.loaded / event.total)));
            } else if (event instanceof HttpResponse) {
              this.setProgress(0);
              if (event.body && event.body.length > 0) {
                this.harnessKits = event.body;
                this.collectErrorMessages(this.harnessKits);
                this.poaKits = new Array<BuildToolHarness>();
                this.vaaKits = new Array<BuildToolHarness>();
                this.showFileInput = false;
                this.showHarnessBuckets = true;
                const programStrings = new Array<string>();
                this.esciService.fetchReferenceData(this, 'programs', programStrings, this.esciService.buildCheckToolProgramListUrl);
                const modelYearStrings = new Array<string>();
                this.esciService.fetchReferenceData(this, 'modelYears', modelYearStrings,
                    this.esciService.buildCheckToolModelYearListUrl);
                this.setupRefDataLists(programStrings, modelYearStrings);
              } else {
                this.messageService.processError('Either no harnesses found or files were invalid. Please try again.');
              }
            }
          },
          error: (err: any) => {
            this.setProgress(0);
            console.log(err);
            this.messageService.processError('Could not run the kit matrix generation. Please check the files provided and try again later.');
          }
        });
      });
    }
  }

  bctDetailsDisabledReason(): string {
    if (!this.harnessMatrixFiles?.length) return 'No harness matrix file selected';
    if (this.harnessMatrixFiles.length > 1) return 'More than one harness matrix file selected';
    if (!this.vehicleBuildFile) return 'No vehicle build file selected';
    return '';
  }

  generateBctDetails(): void {
    if (this.bctDetailsDisabledReason() || !this.adminPermission() || !this.areFileSizesValid()) return;
    this.setProgress(10);
    this.uploadService.uploadFilesForBctDetails(
        this.harnessMatrixFiles[0], this.vehicleBuildFile, this.bypassValidation
    ).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.setProgress(Math.max(10, Math.round(100 * event.loaded / event.total)));
        } else if (event instanceof HttpResponse) {
          this.setProgress(0);
          const fileName = 'BctDetails_' + CommonHelperService.getDateString() + '.xlsx';
          CommonHelperService.downloadFile(event.body, fileName);
          this.messageService.processSuccess('BCT Details successfully generated. Please see the downloaded file.');
        }
      },
      error: (err: any) => {
        this.setProgress(0);
        console.log(err);
        this.messageService.processError('Could not generate BCT Details. Please check the files provided and try again later.');
      }
    });
  }

  generateKitMatrix(): void {
    if (this.harnessMatrixFiles?.length && this.vehicleBuildFile && this.permission() && this.areFileSizesValid()) {
      // this.showHarnessBuckets = false;
      this.setProgress(10);
      // Zip up the harness matrix files and then call the upload function
      const zip = new JSZip();
      for (const file of this.harnessMatrixFiles) {
        zip.file(file.name, file);
      }
      zip.generateAsync({type: 'blob'}).then((content) => {
        this.harnessMatrixFileBlob = content;
        this.uploadService.uploadFilesForKitMatrix(this.vaaKits, this.harnessKits, this.poaKits, this.harnessMatrixFileBlob,
            this.vehicleBuildFile, this.bypassValidation).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.setProgress(Math.max(10, Math.round(100 * event.loaded / event.total)));
            } else if (event instanceof HttpResponse) {
              this.setProgress(0);
              console.log('uploadFilesForKitMatrix success');
              const fileName = this.selectedProgram?.programName + '_' + this.selectedModelYear?.year + '_KitMatrix'
                  + CommonHelperService.getDateString() + '.xlsx';
              CommonHelperService.downloadFile(event.body, fileName);
              this.messageService.processSuccess('Kit Matrix successfully generated. Please see the downloaded file.');
            }
          },
          error: (err: any) => {
            this.setProgress(0);
            console.log(err);
            this.messageService.processError('Could not run the kit matrix generation. Please check the files provided and try again later.');
          }
        });
      });
    }
  }

  ngOnInit(): void {
    this.permission.set(false);
    this.downloadButtonOptions = [
      {
        label: 'Download All Builds',
        icon: 'pi pi-download',
        command: () => {
          this.exportReport(false);
        }
      },
      {
        label: 'Download All Harnesses',
        icon: 'pi pi-download',
        command: () => {
          this.exportAllReports();
        }
      }
    ];
    // Defer permission initialization one macrotask so @if permission-gated blocks
    // do not toggle during Angular's initial dev-mode check cycle (avoids NG0100).
    this.esciService.getPermissionAndFileSizeLimit(this);
    this.esciService.fetchReferenceData(this, 'programs', [], this.esciService.buildCheckToolProgramListUrl);
    this.esciService.fetchReferenceData(this, 'modelYears', [],
        this.esciService.buildCheckToolModelYearListUrl);
  }

  exportReport(onlySelected: boolean) {
    if (!this.selectedHarness || !this.permission()) {
      return;
    }
    let fileName = this.selectedHarness?.harnessName + '_BuildCheckToolReport' + CommonHelperService.getDateString() + '.xlsx';
    const harness = structuredClone(this.selectedHarness);
    if (onlySelected) {
      fileName = this.selectedVehicleBuildOptions[0].vehicleBuildId + '_' +
        this.selectedVehicleBuildOptions[0].partNumber + '_BuildCheckToolReport' + CommonHelperService.getDateString() + '.xlsx';
      const vehicleBuildOptions = new Array<VehicleBuildOptions>();
      vehicleBuildOptions.push(this.selectedVehicleBuildOptions[0]);
      harness.vehicleBuildOptionsList = vehicleBuildOptions;
    }
    this.callDownload(new Array(harness), fileName);
  }

  private callDownload(buildToolHarnesses: BuildToolHarness[], fileName: string) {
    if (this.permission()) {
      this.downloadService.download(buildToolHarnesses)
        .subscribe(blob => {
          CommonHelperService.downloadFile(blob, fileName);
          this.messageService.processSuccess('Report successfully generated. Please see the downloaded file.');
        });
    }
  }

  exportAllReports() {
    const fileName = 'BuildCheckToolReport' + CommonHelperService.getDateString() + '.xlsx';
    const vehicleBuildOptions = new Array<VehicleBuildOptions>();
    this.callDownload(this.harnesses, fileName);
  }

  formatVehicleBuildText(vehicleBuild: VehicleBuildOptions) {
    return vehicleBuild.vehicleBuildId + ' (' + vehicleBuild.partNumber + ') ' +
      vehicleBuild.commonOptions.length + ' Common | ' +
      vehicleBuild.vehicleOnlyElectricalOptions.length + ' Vehicle | ' +
      vehicleBuild.harnessOnlyOptions.length + ' Harness';
  }

  drop(event: CdkDragDrop<BuildToolHarness[], any>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  moveHarnesses(fromArray: Array<BuildToolHarness>, toArray: Array<BuildToolHarness>) {
    toArray.push(...fromArray);
    fromArray.length = 0;
  }

  private areFileSizesValid(): boolean {
    let valid = true;
    if (this.harnessMatrixFiles?.length) {
      if (this.harnessMatrixFiles.length === 1 &&
          this.harnessMatrixFiles[0].size > this.MAX_FILE_SIZE) {
        this.messageService.processError('Harness Matrix file is too large. Please select a smaller file.' + this.maxFileSizeMessage);
        valid = false;
      } else {
        let harnessMatrixFilesSize = 0;
        for (let i = 0; i < this.harnessMatrixFiles.length; i++) {
          harnessMatrixFilesSize += this.harnessMatrixFiles[i].size;
        }
        if (harnessMatrixFilesSize > this.MAX_FILE_SIZE) {
          this.messageService.processError('Harness Matrix files are too large. Please select smaller or fewer files. Maximum combined file size is ' +
              Math.round(this.MAX_FILE_SIZE / 1000) + 'kb.');
          valid = false;
        }
      }
    }
    if (this.vehicleBuildFile && this.vehicleBuildFile.size > this.MAX_FILE_SIZE) {
      this.messageService.processError('Vehicle Build file is too large. Please select a smaller file.' + this.maxFileSizeMessage);
      valid = false;
    }
    if (this.kitMatrixFile && this.kitMatrixFile.size > this.MAX_FILE_SIZE) {
      this.messageService.processError('Kit Matrix file is too large. Please select a smaller file.' + this.maxFileSizeMessage);
      valid = false;
    }
    return valid;
  }

  // The new dropdowns with built-in filters require objects instead of just strings
  private setupRefDataLists(programStrings, modelYearStrings) {
    const programs = new Array<Program>();
    programStrings.map(programName => {
      const p = new Program();
      p.programName = programName;
      programs.push(p);
    });
    this.programs = programs;
    const modelYears = new Array<ModelYear>();
    modelYearStrings.map(modelYear => {
      modelYears.push(new ModelYear(modelYear));
    });
    this.modelYears = modelYears;
  }
}
